import { useState } from 'react';
import {
  Card, CardContent, Typography, Stack, Box, TextField, Button,
  Alert, Skeleton, Chip, Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import { useDetect } from '../hooks/useDetect';
import { normaliseScanInput } from '../api/scanInput';
import { CodeBlock } from './CodeBlock';

export function ScanSection() {
  const { state, isScanning, scan, reset } = useDetect();
  const [url, setUrl] = useState('');

  const onScan = () => {
    const target = normaliseScanInput(url);
    if (!target) return;
    scan(target);
  };

  const statusLabel: Record<string, string> = {
    idle: 'Ready',
    submitting: 'Submitting…',
    queued: 'Queued',
    running: 'Scanning…',
    succeeded: 'Complete',
    failed: 'Failed',
    error: 'Error',
  };

  const statusColor: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
    idle: 'default',
    submitting: 'info',
    queued: 'warning',
    running: 'warning',
    succeeded: 'success',
    failed: 'error',
    error: 'error',
  };

  return (
    <Card>
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h3" sx={{ mb: 3 }}>Scan a store</Typography>
        <Typography variant="body2" sx={{ color: 'var(--ink-muted)', mb: 3 }}>
          Enter a store URL to see the payment methods it accepts, whether it is live, and
          the merchant identifiers its storefront exposes.
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="https://example.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') onScan(); }}
            disabled={isScanning}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: 0 },
            }}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<SearchIcon />}
            onClick={onScan}
            disabled={isScanning || !url.trim()}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Scan
          </Button>
          {isScanning && (
            <Button
              variant="outlined"
              size="small"
              onClick={reset}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Cancel
            </Button>
          )}
        </Stack>

        {state.status !== 'idle' && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={statusLabel[state.status] ?? state.status}
                color={statusColor[state.status] ?? 'default'}
                size="small"
                sx={{ borderRadius: 0 }}
              />
              {state.jobId && (
                <Typography sx={{ fontFamily: 'IBM Plex Mono', fontSize: '0.75rem', color: 'var(--ink-muted)' }}>
                  {state.jobId.slice(0, 8)}…
                </Typography>
              )}
              {state.status === 'running' && state.elapsedS !== undefined && (
                <Typography sx={{ fontFamily: 'IBM Plex Mono', fontSize: '0.75rem', color: 'var(--ink-muted)' }}>
                  {state.elapsedS}s
                </Typography>
              )}
            </Stack>

            {state.status === 'error' && (
              <Alert severity="error" sx={{ borderRadius: 0 }}>
                {state.error ?? 'An error occurred'}
              </Alert>
            )}

            {state.status === 'failed' && (
              <Alert severity="error" sx={{ borderRadius: 0 }}>
                {state.error ?? 'Detection failed'}
              </Alert>
            )}

            {(state.status === 'submitting' || state.status === 'queued' || state.status === 'running') && (
              <Skeleton variant="rectangular" height={120} />
            )}

            {state.status === 'succeeded' && state.result && (
              <Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="h4" sx={{ mb: 1 }}>Result</Typography>
                <CodeBlock code={JSON.stringify(state.result, null, 2)} language="json" maxHeight={400} />
              </Box>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
